<template>
  <div>
    <wc-page-title title='Unpaid invoices'></wc-page-title>
    <v-container>
      <v-row>
        <v-col cols='12' md='8'>
          <wc-data-box
            title='Setup'
            titleIcon='mdi-cogs'
          >
            <v-card-text>
              <v-form v-model='isFormValid'>
                <v-row>
                  <v-col cols='6'>
                    <v-select
                      label='Select time frame'
                      :items='frames'
                      return-object
                      v-model='timeFrame'
                    >
                      <template v-slot:append-outer>
                        <v-btn
                          small
                          depressed
                          color='success'
                          @click='runReport'
                          :loading='is_loading'
                        >Run report</v-btn>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols='6'>
                    <v-checkbox
                      label='Ignore balances $10 and under?'
                      v-model='ignoreSmallBalances'
                      ></v-checkbox>
                  </v-col>
                </v-row>
              </v-form>
              <small class='grey--text'>The results will be based on the "Date Sent" value. Invoices that have not been sent will not be shown.</small>
            </v-card-text>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='4'>
          <wc-data-box
            title='Totals'
          >
            <v-card-text>
              <table width='100%'>
                <tr>
                  <td class='title'>Count</td>
                  <td class='title text-right'>{{ invoices.length }}</td>
                </tr>
                <tr>
                  <td colspan='2'><v-divider class='my-3'></v-divider></td>
                </tr>
                <tr>
                  <td class='title'>Total</td>
                  <td class='title text-right'>{{ reportTotal | decMoney }}</td>
                </tr>
              </table>
            </v-card-text>
          </wc-data-box>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols='12'>
          <wc-data-box
            title='Report Results'
            titleIcon='mdi-finance'
            showSearch
            v-model='search'
          >
            <v-data-table
              :items='invoices'
              :headers='headers'
              :loading='is_loading'
              dense
              :items-per-page='-1'
              disable-pagination
              hide-default-footer
              :search='search'
              @click:row='openInvoice'
            >
              <template v-slot:item.number='{ item }'>
                {{ item.number }}
                <v-icon small v-if="item.status === 'on_hold'" color='error'>{{$icons.hold.icon}}</v-icon>
              </template>
              <template v-slot:item.original_amount='{ item }'>
                {{ item.original_amount | dec }}
              </template>
              <template v-slot:item.amount_os='{ item }'>
                {{ item.amount_os | dec }}
              </template>
              <template v-slot:item.action_1='{ item }'>
                <strong v-if='item.action_1' class='error--text'>A1</strong>
              </template>
              <template v-slot:item.action_2='{ item }'>
                <strong v-if='item.action_2' class='error--text'>A2</strong>
              </template>
              <template v-slot:item.action_3='{ item }'>
                <strong v-if='item.action_3' class='error--text'>A3</strong>
              </template>
              <template v-slot:item.action_4='{ item }'>
                <strong v-if='item.action_4' class='error--text'>A4</strong>
              </template>
            </v-data-table>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import accountingAPI from '@/api/accounting';
import moment from 'moment';
import formRules from '@/mixins/formRules';
import _ from 'lodash';

export default {
  name: 'unpaid-invoices',
  mixins: [formRules],
  data: () => ({
    is_loading: false,
    isFormValid: false,
    invoices: [],
    timeFrame: '',
    search: '', 
    ignoreSmallBalances: true,
    headers: [
      { text: 'Date Sent', value: 'date_sent' },
      { text: 'Number', value: 'number' },
      { text: 'Bill to', value: 'bill_to_display' },
      { text: 'Action 1', value: 'action_1', align: 'center' },
      { text: 'Action 2', value: 'action_2', align: 'center' },
      { text: 'Action 3', value: 'action_3', align: 'center' },
      { text: 'Action 4', value: 'action_4', align: 'center' },
      { text: 'Org Amt', value: 'original_amount', align: 'right' },
      { text: 'OS', value: 'amount_os', align: 'right' }
    ]
  }),
  computed: {
    frames () {
      let today = moment();
      let less30 = moment().subtract(30, 'days');
      let less31 = moment().subtract(31, 'days');
      let less60 = moment().subtract(60, 'days');
      let less61 = moment().subtract(61, 'days');
      let less90 = moment().subtract(90, 'days');
      let less91 = moment().subtract(91, 'days');
      return [
        {
          text: '0 - 30 days',
          start: less30.format('YYYY-MM-DD').toString(),
          end: today.format('YYYY-MM-DD').toString()
        },
        {
          text: '31 - 60 days',
          start: less60.format('YYYY-MM-DD').toString(),
          end: less31.format('YYYY-MM-DD').toString()
        },
        {
          text: '61 - 90 days',
          start: less90.format('YYYY-MM-DD').toString(),
          end: less61.format('YYYY-MM-DD').toString()
        },
        {
          text: 'Over 90 days',
          start: null,
          end: less91.format('YYYY-MM-DD').toString()
        },
        {
          text: 'All',
          start: null,
          end: null
        }
      ]
    },
    reportTotal () {
      return _.sumBy(this.invoices, 'amount_os');
    }
  },
  mounted () {
    this.timeFrame = this.frames[0];
  },
  methods: {
    async runReport () {
      this.is_loading = true;
      this.invoices = await accountingAPI.getUnpaidInvoices(this.timeFrame.start, this.timeFrame.end, this.ignoreSmallBalances);
      this.is_loading = false;
    },
    openInvoice (invoice) {
      const routeData = this.$router.resolve({name: 'InvoiceOverview', params: { id: invoice.id }});
      window.open(routeData.href, '_blank');
    }
  }
}
</script>
